import { parseCreditoMaximoUnitSale } from './creditoMaximoMonto';
import { isLocalCurrencyCode } from './currencyLabel';

export type ProductGrantLine = {
  asset_code?: string;
  currency_code: string;
  cantidad_otorgada: string;
  monto_otorgado: string;
};

const ASSET_CODE_BOL = 'BOL';
const ASSET_CODE_M3H = 'M3H';

function parseGrantAmount(raw: string): number {
  const t = String(raw ?? '').trim().replace(',', '.');
  if (t === '') return 0;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 0;
}

function lineToBolsas(
  line: ProductGrantLine,
  valorBolsa: number,
  valorM3: number,
  bolsaAssetCode: string,
  m3AssetCode: string
): number {
  const code = line.asset_code?.toUpperCase().trim();
  const cant = parseGrantAmount(line.cantidad_otorgada);
  const monto = parseGrantAmount(line.monto_otorgado);
  if (code === bolsaAssetCode) return cant;
  if (code === m3AssetCode) {
    if (valorM3 <= 0 || valorBolsa <= 0) return 0;
    return (cant * valorM3) / valorBolsa;
  }
  // Moneda nacional (ARS Cementos AR / UYU Cementos UY) → bolsas vía valor unitario (#52786)
  if (isLocalCurrencyCode(line.currency_code) && monto > 0 && valorBolsa > 0) {
    return monto / valorBolsa;
  }
  return 0;
}

export function totalBolsasOtorgadas(
  lineas: ProductGrantLine[],
  valorBolsa: number,
  valorM3: number,
  bolsaAssetCode: string = ASSET_CODE_BOL,
  m3AssetCode: string = ASSET_CODE_M3H
): number {
  if (valorBolsa <= 0) return 0;
  return lineas.reduce(
    (sum, line) => sum + lineToBolsas(line, valorBolsa, valorM3, bolsaAssetCode, m3AssetCode),
    0
  );
}

export function totalM3Otorgadas(
  lineas: ProductGrantLine[],
  m3AssetCode: string = ASSET_CODE_M3H
): number {
  const m3 = m3AssetCode.toUpperCase();
  return lineas.reduce((sum, line) => {
    const code = line.asset_code?.toUpperCase().trim();
    if (code === m3) return sum + parseGrantAmount(line.cantidad_otorgada);
    return sum;
  }, 0);
}

export function resolveQuotationValues(
  assets: Array<{ code: string; saleValue?: number | string }>
): { valorBolsa: number; valorM3: number } {
  const bol = assets.find((a) => a.code === ASSET_CODE_BOL);
  const m3 = assets.find((a) => a.code === ASSET_CODE_M3H);
  return {
    valorBolsa: parseCreditoMaximoUnitSale(bol?.saleValue) ?? 0,
    valorM3: parseCreditoMaximoUnitSale(m3?.saleValue) ?? 0,
  };
}

export function computeSuperaMaximo(params: {
  productos: ProductGrantLine[];
  creditoMaximoUnidades: number;
  unidadCalculoCode: string;
  valorBolsa: number;
  valorM3: number;
}): boolean {
  const max = params.creditoMaximoUnidades;
  if (!(max > 0)) return false;

  if (params.unidadCalculoCode.toUpperCase() === ASSET_CODE_M3H) {
    return totalM3Otorgadas(params.productos) > max;
  }

  return (
    totalBolsasOtorgadas(params.productos, params.valorBolsa, params.valorM3) > max
  );
}
